import { apiClient } from './client';

// Request types
export interface UpdateLocationRequest {
  latitude: number;
  longitude: number;
  accuracy: number;
  heading?: number;
  speed?: number;
}

export interface DiscoverPinRequest {
  latitude: number;
  longitude: number;
}

// Response types
export interface UpdateLocationResponse {
  updated: boolean;
}

export interface DiscoverPinResponse {
  discovered: boolean;
  pinId: string;
  hintPhotoUrl: string | null;
  itemCount: number;
  foundItemsAtCurrentPin: number;
}

export interface MarkItemFoundResponse {
  itemIndex: number;
  totalFoundAtPin: number;
  totalFoundOverall: number;
  pinComplete: boolean;
  challengeRequired?: boolean;
  challengeType?: 'riddle' | 'hex_code' | 'numerical';
  challengeQuestion?: string;
}

export interface CompletePinResponse {
  nextPinIndex: number | null;
  huntComplete: boolean;
  completionTime?: string;
}

export interface HunterPinInfo {
  currentPinIndex: number;
  totalPins: number;
  currentPin: {
    id: string;
    latitude: number;
    longitude: number;
    itemCount: number;
    hintPhotoUrl: string | null;
  } | null;
  foundItemsAtCurrentPin: number;
  /** Whether the hunter has previously discovered (been within 25m of) the current pin */
  isDiscovered?: boolean;
  /** Fresh SAS URL for hint photo — only populated when isDiscovered is true */
  hintPhotoUrl?: string | null;
  /** Active or solved challenge state for the current pin (for reconnect restoration) */
  challengeState?: {
    isActive: boolean;
    isSolved: boolean;
    lockedByUserId?: string;
    lockedByDisplayName?: string;
    challenge?: { type: string; question: string };
  } | null;
  /** Buddy pairing info (for GPS-denied hunters) */
  buddyUserId?: string;
  buddyDisplayName?: string;
}

/**
 * Hunter API service
 */
export const hunterService = {
  /**
   * Update hunter's location
   */
  async updateLocation(
    huntId: string,
    data: UpdateLocationRequest
  ): Promise<UpdateLocationResponse> {
    const response = await apiClient.post<UpdateLocationResponse>(
      `/hunts/${huntId}/location`,
      data
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update location');
    }

    return response.data!;
  },

  /**
   * Discover a pin (when within 25m proximity threshold)
   */
  async discoverPin(
    huntId: string,
    pinId: string,
    data: DiscoverPinRequest
  ): Promise<DiscoverPinResponse> {
    const response = await apiClient.post<DiscoverPinResponse>(
      `/hunts/${huntId}/pins/${pinId}/discover`,
      data
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to discover pin');
    }

    return response.data!;
  },

  /**
   * Mark an item as found at a pin
   */
  async markItemFound(
    huntId: string,
    pinId: string,
    itemIndex: number
  ): Promise<MarkItemFoundResponse> {
    const response = await apiClient.post<MarkItemFoundResponse>(
      `/hunts/${huntId}/pins/${pinId}/items/${itemIndex}/found`,
      {}
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to mark item as found');
    }

    return response.data!;
  },

  /**
   * Complete a pin and advance to next
   */
  async completePin(
    huntId: string,
    pinId: string
  ): Promise<CompletePinResponse> {
    const response = await apiClient.post<CompletePinResponse>(
      `/hunts/${huntId}/pins/${pinId}/complete`,
      {}
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to complete pin');
    }

    return response.data!;
  },

  /**
   * Get current pin info for hunter
   */
  async getCurrentPin(huntId: string): Promise<HunterPinInfo> {
    const response = await apiClient.get<HunterPinInfo>(
      `/hunts/${huntId}/pins`
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to get pin info');
    }

    return response.data!;
  },

  /**
   * Pair with a GPS-equipped buddy (for GPS-denied hunters)
   */
  async pairBuddy(
    huntId: string,
    buddyUserId: string
  ): Promise<{ paired: boolean; buddyUserId: string; buddyDisplayName: string }> {
    const response = await apiClient.post<{
      paired: boolean;
      buddyUserId: string;
      buddyDisplayName: string;
    }>(`/hunts/${huntId}/pair-buddy`, { buddyUserId });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to pair with buddy');
    }

    return response.data!;
  },
};
